import { useEffect, useRef, useState } from "react";
import type { ChatModule } from "../api";
import { SectionLabel } from "./chrome";

/* ═══════════════════════════════════════════════════════════════════════
   What your chat will be able to do.

   Shown only in the setup conversation, to a business owner. Their
   customers never see it — see `willHave` in the API, which is null for
   every pack but this one.

   ── Why it is a panel and not something crAIg says ─────────────────────
   A business owner has no idea crAIg has modules, and telling them costs
   money: reading a dozen summaries aloud is output tokens on every single
   setup, takes minutes, and is worse than something they can scan in ten
   seconds. So crAIg keeps asking his ordinary questions, and the chips
   appear beside the answer that caused them. Nothing to click, nothing to
   choose from, no menu read out.

   Everything here comes from the server, which derives it from the same
   `build()` that publishing uses — so this is not a preview of what they
   might get, it is the thing itself, early.

   ── Two layouts, and the phone is the real one ─────────────────────────
   The whole value is being noticed without being announced, so a
   collapsed corner button — the file drawer's pattern — would defeat it:
   that is a thing you have to decide to open. On a phone this sits
   directly above the composer, which is where the eye already rests
   between turns, so a chip arriving lands in peripheral vision. On a wide
   screen there is room to just show it.
   ═══════════════════════════════════════════════════════════════════════ */

export function ChatModules({
  modules,
  wide,
}: {
  modules: ChatModule[];
  wide: boolean;
}) {
  const [openSheet, setOpenSheet] = useState(false);

  /*
    Reserve the height the moment the first chip lands, and never give it
    back.

    A strip that grows as chips arrive re-flows the thread under somebody
    who is halfway through reading a sentence, which on a phone is the
    difference between delightful and infuriating. Once there has been
    something to show, the space stays spoken for.
  */
  const everHad = useRef(false);
  if (modules.length) everHad.current = true;

  useEffect(() => {
    document.body.style.overflow = openSheet ? "hidden" : "";
    return () => void (document.body.style.overflow = "");
  }, [openSheet]);

  /* Nothing has been settled yet — not even which pack they're heading
     for. Say so in one line rather than rendering an empty box, and only
     on a wide screen: on a phone this would eat the composer's breathing
     room to say nothing. */
  if (!modules.length && !everHad.current) {
    return wide ? (
      <aside className="sticky top-4 shrink-0" style={{ width: 260 }}>
        <div className="card px-4 py-4">
          <SectionLabel>Your chat</SectionLabel>
          <p style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 8, lineHeight: 1.5 }}>
            As we talk, I'll show what your chat will be able to do.
          </p>
        </div>
      </aside>
    ) : null;
  }

  /* ── Wide: a panel, summaries already visible ────────────────────────── */

  if (wide) {
    return (
      <aside className="sticky top-4 shrink-0" style={{ width: 260 }}>
        <div className="card px-4 py-4">
          <SectionLabel>Your chat will</SectionLabel>
          <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0" }}>
            {modules.map((m, i) => (
              <li
                key={m.id}
                className="fade-up"
                style={{ animationDelay: `${i * 0.04}s`, marginBottom: 12 }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 650 }}>{m.label}</div>
                <div
                  style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.45, marginTop: 2 }}
                >
                  {m.summary}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    );
  }

  /* ── Phone: one line above the composer, tap for the detail ──────────── */

  return (
    <>
      <div
        style={{
          borderTop: "1px solid var(--line)",
          background: "var(--surface-2)",
          /* Spoken for from the first chip onwards — see everHad. */
          minHeight: 44,
        }}
      >
        <button
          type="button"
          onClick={() => setOpenSheet(true)}
          aria-label={`What your chat will do — ${modules.length} so far`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            background: "none",
            border: "none",
            padding: "8px 16px",
            cursor: "pointer",
            /* One line, scrolling sideways. Wrapping would grow the strip
               and push the thread, which is the thing being avoided. */
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 650,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--ink-3)",
              flexShrink: 0,
            }}
          >
            Your chat will
          </span>
          {modules.map((m, i) => (
            <span
              key={m.id}
              className="chip fade-up"
              style={{ animationDelay: `${i * 0.04}s`, flexShrink: 0 }}
            >
              {m.label}
            </span>
          ))}
        </button>
      </div>

      {openSheet && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpenSheet(false)}
            style={{ background: "rgba(20,20,18,.4)" }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="What your chat will do"
            className="fixed inset-x-0 bottom-0 z-50 scroll-y safe-b"
            style={{
              background: "var(--surface)",
              borderTop: "1px solid var(--line)",
              borderRadius: "14px 14px 0 0",
              maxHeight: "72vh",
              padding: "18px 18px 8px",
            }}
          >
            <SectionLabel>Your chat will</SectionLabel>
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
              {modules.map((m) => (
                <li key={m.id} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 650 }}>{m.label}</div>
                  <div
                    style={{ fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.5, marginTop: 3 }}
                  >
                    {m.summary}
                  </div>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setOpenSheet(false)}
              className="btn btn-quiet btn-term"
              style={{ width: "100%", marginTop: 4, marginBottom: 8 }}
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
